/**
 * Anchor watch — swing radius and drag detection.
 *
 * When we drop the hook we let out a length of rode (the SCOPE). The boat then
 * swings around the anchor on an arc. The horizontal radius of that arc is the
 * rode length with the vertical part (the water depth plus the height of the bow
 * roller above the water, and any tide still to come) taken out by Pythagoras,
 * plus the boat's own length. If the boat ever sits outside that circle (with a
 * small buffer for GPS wander), the anchor is dragging and we want to know.
 */
import { rhumbDistanceNm, rhumbDestination } from '@/nav';
import { insideZone } from './anchorZone';

const M_PER_NM = 1852;

export interface SwingInput {
  /** Rode let out (chain + warp), metres. */
  scopeM: number;
  /** Water depth under the bow now, metres. */
  depthM: number;
  /** Height of the bow roller above the water, metres. */
  bowHeightM: number;
  /** Extra tide expected to rise before we leave, metres (adds to the vertical). */
  tideRiseM?: number;
  /** Boat length, added to the swing radius, metres. */
  boatLengthM?: number;
}

/** Horizontal swing radius from the anchor, metres. */
export function swingRadiusM(i: SwingInput): number {
  const vertical = i.depthM + i.bowHeightM + (i.tideRiseM ?? 0);
  const horizontal = Math.sqrt(Math.max(0, i.scopeM * i.scopeM - vertical * vertical));
  return horizontal + (i.boatLengthM ?? 0);
}

/** Horizontal swing radius from the anchor, nautical miles. */
export function swingRadiusNm(i: SwingInput): number {
  return swingRadiusM(i) / M_PER_NM;
}

export interface DragCheck {
  /** Distance from the anchor to the boat now, nm. */
  distanceNm: number;
  /** The allowed swing radius, nm. */
  radiusNm: number;
  /** How much room is left before the buffer is breached, nm (negative = over). */
  marginNm: number;
  /** True once the boat is beyond the swing radius plus the buffer. */
  dragging: boolean;
}

/** Compare the boat's position to the swing circle (plus a GPS-wander buffer). */
export function dragCheck(
  anchor: [number, number],
  boat: [number, number],
  radiusNm: number,
  bufferNm = 0,
): DragCheck {
  const distanceNm = rhumbDistanceNm(anchor, boat);
  const limit = radiusNm + bufferNm;
  return { distanceNm, radiusNm, marginNm: limit - distanceNm, dragging: distanceNm > limit };
}

/** The swing-circle outline ([lon,lat]) around the anchor, for drawing. */
export function swingCircle(
  anchor: [number, number],
  radiusNm: number,
  steps = 64,
): [number, number][] {
  const ring: [number, number][] = [];
  for (let k = 0; k < steps; k++) {
    ring.push(rhumbDestination(anchor, (360 * k) / steps, radiusNm));
  }
  ring.push(ring[0]);
  return ring;
}

/**
 * Place the anchor a bearing and distance off the boat. Handy when we motored up
 * to a spot, dropped, then dropped back on the rode: the anchor isn't under us
 * any more, it's a known bearing and distance off the bow. Distance is in metres
 * (the same unit as scope and depth on the sheet); rhumbDestination works in
 * nautical miles, so we convert.
 */
export function anchorFromBearing(
  from: [number, number],
  bearingDeg: number,
  distanceM: number,
): [number, number] {
  return rhumbDestination(from, bearingDeg, distanceM / M_PER_NM);
}

/**
 * Is a point inside a zone boundary? Delegates to insideZone, which tests the
 * CURVE through the points rather than the straight lines between them, because
 * that curve is what gets drawn on the chart. There were two ray-casts here
 * once, one for the alarm and one for the geometry, and the moment the drawn
 * boundary stopped being the straight-line one they would have disagreed. A
 * boat sitting visibly inside its boundary with the alarm going is how a crew
 * learns to ignore the alarm.
 *
 * Fewer than three points is not a boundary, so nothing is inside it.
 */
export function pointInZone(zone: [number, number][], p: [number, number]): boolean {
  if (zone.length < 3) return false;
  return insideZone(zone, p);
}

/**
 * True when the boat has left a valid custom anchor zone (three vertices or more)
 * — the drag condition for the advanced zone mode. A zone with fewer than three
 * points isn't a boundary yet, so we never call that a drag.
 */
export function outsideZone(zone: [number, number][], boat: [number, number]): boolean {
  return zone.length >= 3 && !pointInZone(zone, boat);
}
